import React, { useEffect, useState } from 'react'
import type { GridController } from '../../compositor/GridController'
import type { SpreadsheetNamedRangeEntry } from '../../protocol/spreadsheetEngineProtocol'
import type { FormulaEditSession, FormulaCommitNavigation } from '../../spreadsheet/formulaAuthoring'
import { formatSelectionAddress, parseA1Selection, type SpreadsheetSelectionRange } from '../../spreadsheet/addressing'
import FormulaBar from '../FormulaBar'
import { getFuseGridButtonIcon } from '../icons/FuseGridButtonIcons'
import './ExcelWorkbookChrome.css'

type ExcelFormulaBarRowProps = {
  controller: GridController
  selectedRange: SpreadsheetSelectionRange | null
  rowCount: number
  colCount: number
  liveValue?: string | null
  formulaSession?: FormulaEditSession | null
  currentSheetName?: string
  expanded: boolean
  definedNames?: SpreadsheetNamedRangeEntry[]
  tableNames?: Array<{
    name: string
    range: SpreadsheetSelectionRange
  }>
  formulaDefinedNames?: Array<{
    name: string
    scopeLabel?: string
    refersTo?: string
    hidden?: boolean
  }>
  onToggleExpanded: () => void
  onNavigateToSelection: (selection: SpreadsheetSelectionRange) => void
  onNavigateToDefinedName?: (entry: SpreadsheetNamedRangeEntry) => void
  onAcceptFormula: (navigation?: FormulaCommitNavigation | null) => void
  onCancelFormula: () => void
  onBeginFormulaEdit?: (value: string, caretStart: number, caretEnd: number) => void
  onDraftChange?: (value: string, caretStart: number, caretEnd: number) => void
  onFormulaSessionChange?: (session: FormulaEditSession) => void
  onFinishFormulaEdit?: () => void
  showFunctionAssistant?: boolean
  onActivateFormulaSurface?: () => void
  onOpenInsertFunction?: () => void
}

const CancelIcon = getFuseGridButtonIcon('close')!
const AcceptIcon = getFuseGridButtonIcon('accept')!
const ExpandIcon = getFuseGridButtonIcon('chevron-down')!
const CollapseIcon = getFuseGridButtonIcon('chevron-up')!

const selectionsMatch = (
  left: SpreadsheetSelectionRange | null | undefined,
  right: SpreadsheetSelectionRange | null | undefined,
): boolean => (
  Boolean(left && right)
  && left!.col === right!.col
  && left!.row === right!.row
  && left!.endCol === right!.endCol
  && left!.endRow === right!.endRow
)

export function ExcelFormulaBarRow({
  controller,
  selectedRange,
  rowCount,
  colCount,
  liveValue,
  formulaSession,
  currentSheetName,
  expanded,
  definedNames = [],
  tableNames = [],
  formulaDefinedNames = [],
  onToggleExpanded,
  onNavigateToSelection,
  onNavigateToDefinedName,
  onAcceptFormula,
  onCancelFormula,
  onBeginFormulaEdit,
  onDraftChange,
  onFormulaSessionChange,
  onFinishFormulaEdit,
  showFunctionAssistant,
  onActivateFormulaSurface,
  onOpenInsertFunction,
}: ExcelFormulaBarRowProps) {
  const selectionLabel = formatSelectionAddress(selectedRange, { colCount, rowCount })
  const selectedTableName = tableNames.find((entry) => selectionsMatch(entry.range, selectedRange))?.name ?? ''
  const nameBoxDisplayValue = selectedTableName || selectionLabel
  const [nameBoxValue, setNameBoxValue] = useState(nameBoxDisplayValue)

  useEffect(() => {
    setNameBoxValue(nameBoxDisplayValue)
  }, [nameBoxDisplayValue])

  const commitNameBox = () => {
    const requestedName = String(nameBoxValue || '').trim()
    const namedRange = definedNames.find((entry) => entry.name.toLowerCase() === requestedName.toLowerCase())
    if (namedRange && onNavigateToDefinedName) {
      onNavigateToDefinedName(namedRange)
      return
    }
    const tableName = tableNames.find((entry) => entry.name.toLowerCase() === requestedName.toLowerCase())
    if (tableName) {
      onNavigateToSelection(tableName.range)
      return
    }
    const parsed = parseA1Selection(nameBoxValue, { colCount, rowCount })
    if (!parsed) {
      setNameBoxValue(nameBoxDisplayValue)
      return
    }
    onNavigateToSelection(parsed)
  }

  const formulaIsActive = Boolean(formulaSession && formulaSession.status === 'active')

  return (
    <div className="fg-excel-chrome fg-excel-formula-row" data-testid="fg-excel-formula-row">
      <label className="fg-excel-name-box" title="Name Box">
        <input
          value={nameBoxValue}
          onChange={(event) => setNameBoxValue(event.target.value)}
          onFocus={(event) => event.currentTarget.select()}
          onBlur={() => setNameBoxValue(nameBoxDisplayValue)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              commitNameBox()
              event.currentTarget.blur()
            } else if (event.key === 'Escape') {
              event.preventDefault()
              setNameBoxValue(nameBoxDisplayValue)
              event.currentTarget.blur()
            }
          }}
          aria-label="Name Box"
          title="Name Box"
          list="fg-excel-defined-name-list"
          spellCheck={false}
          data-lpignore="true"
          autoComplete="off"
        />
        {(definedNames.length > 0 || tableNames.length > 0) && (
          <datalist id="fg-excel-defined-name-list">
            {definedNames.map((entry) => (
              <option key={entry.name} value={entry.name} />
            ))}
            {tableNames.map((entry) => (
              <option key={`table:${entry.name}`} value={entry.name} />
            ))}
          </datalist>
        )}
      </label>
      <div className="fg-excel-formula-controls" aria-label="Formula controls">
        <button
          type="button"
          className="fg-excel-icon-button"
          title="Cancel formula edit"
          aria-label="Cancel formula edit"
          disabled={!formulaIsActive}
          onClick={onCancelFormula}
        >
          <CancelIcon size={16} />
        </button>
        <button
          type="button"
          className="fg-excel-icon-button"
          title="Accept formula edit"
          aria-label="Accept formula edit"
          disabled={!formulaIsActive}
          onClick={() => onAcceptFormula('none')}
        >
          <AcceptIcon size={16} />
        </button>
      </div>
      <FormulaBar
        controller={controller}
        liveValue={liveValue}
        formulaSession={formulaSession}
        currentSheetName={currentSheetName}
        expanded={expanded}
        definedNames={formulaDefinedNames}
        onBeginFormulaEdit={onBeginFormulaEdit}
        onDraftChange={onDraftChange}
        onFormulaSessionChange={onFormulaSessionChange}
        onFinishFormulaEdit={onFinishFormulaEdit}
        showFunctionAssistant={showFunctionAssistant}
        onActivateFormulaSurface={onActivateFormulaSurface}
        onOpenInsertFunction={onOpenInsertFunction}
      />
      <div className="fg-excel-formula-expand">
        <button
          type="button"
          className="fg-excel-icon-button"
          title={expanded ? 'Collapse formula bar (Ctrl+Shift+U)' : 'Expand formula bar (Ctrl+Shift+U)'}
          aria-label={expanded ? 'Collapse formula bar' : 'Expand formula bar'}
          aria-pressed={expanded}
          onClick={onToggleExpanded}
        >
          {expanded ? <CollapseIcon size={16} /> : <ExpandIcon size={16} />}
        </button>
      </div>
    </div>
  )
}
